import React from "react";
import {ProductComponentState, ProductState} from "../interfaces/ProductComponentState";
import ProductRepository from "../repository/ProductRepository";
import LocalStorageRepository from "../../../repository/LocalStorageRepository";
import {get, random, toNumber} from "lodash";
import AppCartController from "../../Main/controller/AppCartController";
import AppSettingController from "../../Main/controller/AppSettingController";
import InvoiceInterface from "../../Invoice/interface/invoiceInterface";
import i18n from "../../../i18n";

export default class ProductController {
    protected state: ProductComponentState;
    protected repository: ProductRepository;
    protected setState: React.Dispatch<React.SetStateAction<ProductComponentState>>;

    constructor(state: ProductComponentState, setState: React.Dispatch<React.SetStateAction<ProductComponentState>>) {
        this.state = state;
        this.setState = setState;
        this.repository = new ProductRepository();
    }

    async initProduct(categoryId: string, shopId?: string) {
        this.updateState({error: false, loading: true, hasMore: true, products: []});

        if (!shopId) {
            return this.updateState({loading: false, error: true, message: i18n.t("error.invalidShopLink")});
        }

        const regexObjectId = /^[a-f\d]{24}$/i

        if (!regexObjectId.test(shopId)) {
            return this.updateState({loading: false, error: true, message: i18n.t("error.invalidShopLink")});
        }

        const data = await this.repository.all(shopId, categoryId);

        if (!get(data.data, "data", []).length) {
            return this.updateState({loading: false, error: true, message: i18n.t("error.noProductsFound")});
        }

        if (get(data, "status", false)) {
            const products = get(data.data, "data", []);
            
            this.updateState({
                loading: false,
                error: false,
                totalPage: get(data.data, "last_page"),
                currentPage: get(data.data, "current_page"),
                products: products,
            });
        }
    }

    async loadMoreProducts(shopId: string, page: number, categoryId: string) {
        if (!shopId) return;

        this.updateState({loading: true});
        const regexObjectId = /^[a-f\d]{24}$/i

        if (!regexObjectId.test(shopId)) {
            return this.updateState({loading: false, error: true, message: i18n.t("error.invalidShopLink")});
        }

        const data = await this.repository.loadMore(shopId, page, categoryId);

        console.log('Load more products response:', {
            page,
            status: data.status,
            dataLength: data.data?.data?.length,
            lastPage: data.data?.last_page,
            totalPages: data.data?.total_pages,
            currentPage: data.data?.current_page
        });

        if (data.status && data.data?.data?.length) {
            const isLastPage = page >= (data.data.last_page || data.data.total_pages || 1);
            
            const existingIds = new Set(this.state.products.map(p => p._id));
            const newProducts = data.data.data.filter((p: ProductState) => !existingIds.has(p._id));

            this.updateState({
                loading: false,
                products: [...this.state.products, ...newProducts],
                currentPage: page,
                hasMore: !isLastPage,
            });
        } else {
            this.updateState({
                loading: false,
                hasMore: false,
            });
        }
    }

    async customChangePage(shopId: string, categoryId: string) {
        const maxPagesToLoad = 3;
        let pagesLoaded = 0;
        
        while (pagesLoaded < maxPagesToLoad && this.state.hasMore && !this.state.loading) {
            await this.loadMoreProducts(shopId, this.state.currentPage + 1, categoryId);
            pagesLoaded++;
            
            if (document.body.scrollHeight > window.innerHeight) {
                break;
            }
        }
    }

    async handleScroll(shopId: string, categoryId: string) {
        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.body.offsetHeight;
        const threshold = 500;
        
        if (
            scrollPosition >= documentHeight - threshold &&
            this.state.hasMore &&
            !this.state.loading
        ) {
            console.log('Loading more products...', {
                currentPage: this.state.currentPage,
                hasMore: this.state.hasMore,
                loading: this.state.loading
            });
            await this.loadMoreProducts(shopId, this.state.currentPage + 1, categoryId);
        }
    }

    static productArrangementToggle() {
        const setting = JSON.parse(LocalStorageRepository.get("setting") as string);
        LocalStorageRepository.set("setting", {...setting, isList: !setting.isList});
        return !setting.isList;
    }

    static addToCart(item: ProductState, state: boolean, setState: React.Dispatch<React.SetStateAction<boolean>>) {
        if (!AppSettingController.isActiveCart()) return null
        setState(!state)
        return toNumber(item.total) > 0 ? AppCartController.addItem(item) : null
    }

    static removeToCart(item: ProductState, state: boolean, setState: React.Dispatch<React.SetStateAction<boolean>>) {
        setState(!state)
        AppCartController.removeItem(item)
    }

    static addToInvoiceCart(item: ProductState, state: InvoiceInterface, setState: React.Dispatch<React.SetStateAction<InvoiceInterface>>) {
        if (state.loading) return null
        setState({...state, reRender: random(999999999)})
        return toNumber(item.total) > 0 ? AppCartController.addItem(item) : null
    }

    static removeToInvoiceCart(item: ProductState, state: InvoiceInterface, setState: React.Dispatch<React.SetStateAction<InvoiceInterface>>) {
        if (state.loading) return null
        setState({...state, reRender: random(999999999)})
        AppCartController.removeItem(item)
    }

    private updateState(state: object) {
        const newState = {...this.state, ...state};
        this.state = newState;
        this.setState(newState);
    }
}
